import { defineComponent, ComponentPublicInstance, createApp, reactive } from 'vue';
import TModal from './index.vue';
import mitt, { Emitter } from 'mitt';

const eventKey = 'vbui-modal';
type ModalResult = 'confirm' | 'close' | 'cancel';
type IEventHandler = (result: ModalResult | undefined) => void;

interface IModalParams {
  modelValue?: boolean;
  title?: string;
  iconName?: string;
  content?: string | string[];
  richText?: string;
  footer?: boolean;
  isNeedClose?: boolean;
  isNeedCloseCancel?: boolean;
  cancelText?: string;
  confirmText: string;
  iconTheme?: string;
  theme?: string;
  align?: string;
  size?: 'medium' | 'large';
}
let emitter: Emitter | undefined;

function createModal() {
  const state = reactive({
    modelValue: TModal.props.modelValue.default,
    iconName: TModal.props.iconName.default,
    title: TModal.props.title.default,
    content: TModal.props.content.default,
    richText: TModal.props.richText.default,
    footer: TModal.props.footer.default,
    isNeedClose: TModal.props.isNeedClose.default,
    isNeedCloseCancel: TModal.props.isNeedCloseCancel.default,
    cancelText: TModal.props.cancelText.default,
    confirmText: TModal.props.confirmText.default,
    iconTheme: TModal.props.iconTheme.default,
    theme: TModal.props.theme.default,
    align: TModal.props.align.default,
    size: TModal.props.size.default,
  });

  const confirmHandler = () => {
    if (emitter) {
      emitter.emit(eventKey, 'confirm');
    }
  };

  const cancelHandler = () => {
    if (emitter) {
      emitter.emit(eventKey, 'cancel');
    }
  };

  const closeHandler = () => {
    if (emitter) {
      emitter.emit(eventKey, 'close');
    }
  };

  let instance: ComponentPublicInstance | undefined = undefined;
  const _modal = defineComponent({
    components: { TModal },
    setup() {
      return () => (
        <t-modal
          v-model={state.modelValue}
          icon-name={state.iconName}
          icon-theme={state.iconTheme}
          theme={state.theme}
          footer={state.footer}
          is-need-close={state.isNeedClose}
          is-need-close-cancel={state.isNeedCloseCancel}
          size={state.size}
          align={state.align}
          title={state.title}
          content={state.content}
          richText={state.richText}
          cancel-text={state.cancelText}
          confirm-text={state.confirmText}
          onClose={closeHandler}
          onConfirm={confirmHandler}
          onCancel={cancelHandler}
        />
      );
    },
  });

  const show = async (params: IModalParams) => {
    if (!instance) {
      const div = document.createElement('div');
      instance = createApp(_modal).mount(div);
      document.body.appendChild(instance.$el);
    }
    state.modelValue = true;
    state.iconName = params.iconName || TModal.props.iconName.default;
    state.title = params.title || TModal.props.title.default;
    state.content = params.content;
    state.richText = params.richText;
    state.cancelText = params.cancelText;
    state.confirmText = params.confirmText;
    state.footer = params.footer || TModal.props.footer.default;
    state.isNeedClose = params.isNeedClose || TModal.props.isNeedClose.default;
    state.isNeedCloseCancel = params.isNeedCloseCancel || TModal.props.isNeedCloseCancel.default;
    state.iconTheme = params.iconTheme || TModal.props.iconTheme.default;
    state.theme = params.theme || TModal.props.theme.default;
    state.align = params.align || TModal.props.align.default;
    state.size = params.size || TModal.props.size.default;

    return new Promise<ModalResult>(resolve => {
      emitter = mitt();
      const handler: IEventHandler = result => {
        if (emitter) {
          emitter.off(eventKey, handler);
        }
        resolve(result || 'confirm');
      };
      emitter.on<ModalResult>(eventKey, handler);
    });
  };
  const hide = () => {
    state.modelValue = false;
    if (emitter) {
      emitter.emit(eventKey, 'close');
    }
  };

  return { show, hide };
}

export const modal = createModal();
